/**

 * Narrative Writing route helpers (Phase 2C/2E).

 * help_session and draftId must be opaque IDs only — never story text.

 */



import type { NavigateFunction } from 'react-router-dom';

import { ROUTES } from '../../utils/constants';

import type { MalayNarrativeWritingActivityKey } from './malayNarrativeWritingActivities';

import { isMalayNarrativeWritingActivityKey } from './malayNarrativeWritingActivities';

import type { ResolvedLearnerCurriculum } from '../curriculum/learnerCurriculumTypes';

import { isEffectiveMalayLearner } from '../curriculum/learnerCurriculumOverride';



const UUID_RE =

  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;



/** Opaque draft ids: nd_ + hex (backend) or similar alphanumeric, max ~80 chars. */

const OPAQUE_DRAFT_ID_RE = /^nd_[0-9a-f]{16,64}$/i;

const OPAQUE_DRAFT_ID_LOOSE_RE = /^[A-Za-z0-9_-]{8,80}$/;



export function isOpaqueHelpSessionId(value: string | null | undefined): boolean {

  const v = String(value || '').trim();

  return UUID_RE.test(v);

}



/**

 * Accept opaque draft identifiers only. Reject story-like strings (spaces, length > 80).

 */

export function isOpaqueNarrativeDraftId(value: string | null | undefined): boolean {

  const v = String(value || '').trim();

  if (!v) return false;

  if (v.length > 80) return false;

  if (/\s/.test(v)) return false;

  if (OPAQUE_DRAFT_ID_RE.test(v)) return true;

  if (!OPAQUE_DRAFT_ID_LOOSE_RE.test(v)) return false;

  if (/[.,;:!?'"]/.test(v)) return false;

  return true;

}



export function buildNarrativeWritingTutorUrl(helpSessionId?: string | null): string {

  const base = ROUTES.STUDENT_NARRATIVE_WRITING_TUTOR;

  const id = String(helpSessionId || '').trim();

  if (!id) return base;

  if (!isOpaqueHelpSessionId(id)) return base;

  return `${base}?help_session=${encodeURIComponent(id)}`;

}



export function buildNarrativeWritingWriteUrl(draftId?: string | null): string {

  const base = ROUTES.STUDENT_NARRATIVE_WRITING_WRITE;

  const id = String(draftId || '').trim();

  if (!id) return base;

  if (!isOpaqueNarrativeDraftId(id)) return base;

  return `${base}/${encodeURIComponent(id)}`;

}



/** Fixed hub target when a Narrative Learning session ends. */
export function navigateToNarrativeWritingActivitiesHub(navigate: NavigateFunction): void {
  navigate(ROUTES.STUDENT_NARRATIVE_WRITING, { replace: true });
}

/** Fixed target for Narrative Learning Tutor "Back to Activities" — Writing Studio entry. */
export function navigateToNarrativeWritingActivitiesBack(navigate: NavigateFunction): void {
  navigate(ROUTES.STUDENT_NARRATIVE_WRITING_WRITE, { replace: true });
}

export function narrativeWritingPathForActivity(

  key: MalayNarrativeWritingActivityKey

): string {

  switch (key) {

    case 'malay_narrative_learning_tutor':

      return ROUTES.STUDENT_NARRATIVE_WRITING_TUTOR;

    case 'malay_narrative_writing_studio':

      return ROUTES.STUDENT_NARRATIVE_WRITING_WRITE;

    default: {

      const _exhaustive: never = key;

      return _exhaustive;

    }

  }

}



export type NarrativeWritingLandingView =

  | { kind: 'loading' }

  | { kind: 'redirect_home' }

  | { kind: 'landing' };



/**

 * Resolve landing view.

 * Explicit Tamil learners are denied; legacy Malay tenant behaviour is preserved.

 */

export function resolveNarrativeWritingLandingView(opts: {

  labelsLoading: boolean;

  legacyTenantIsMalay: boolean;

  curriculum: ResolvedLearnerCurriculum | null;

}): NarrativeWritingLandingView {

  if (opts.labelsLoading) return { kind: 'loading' };

  if (

    !isEffectiveMalayLearner({

      curriculum: opts.curriculum,

      legacyTenantIsMalay: opts.legacyTenantIsMalay,

    })

  ) {

    return { kind: 'redirect_home' };

  }

  return { kind: 'landing' };

}



export function parseHelpSessionQuery(

  raw: string | null | undefined

): { ok: true; id: string } | { ok: false; reason: 'missing' | 'invalid' } {

  const v = String(raw || '').trim();

  if (!v) return { ok: false, reason: 'missing' };

  if (!isOpaqueHelpSessionId(v)) return { ok: false, reason: 'invalid' };

  return { ok: true, id: v };

}



export function parseNarrativeDraftIdParam(

  raw: string | null | undefined

): { ok: true; id: string } | { ok: false; reason: 'missing' | 'invalid' } {

  const v = String(raw || '').trim();

  if (!v) return { ok: false, reason: 'missing' };

  if (!isOpaqueNarrativeDraftId(v)) return { ok: false, reason: 'invalid' };

  return { ok: true, id: v };

}



export function isNarrativeWritingActivityKeyParam(

  value: string | null | undefined

): value is MalayNarrativeWritingActivityKey {

  return isMalayNarrativeWritingActivityKey(value);

}

